import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { ContextService } from 'src/app/core/service/api/context.service';
import { PubsubService } from 'src/app/core/service/api/pubsub.service';
import { CachedService } from 'src/app/core/service/common/cached.service';
import { ParentTabbedPage } from 'src/app/core/util/abstract/parent-tabbed-page';
import { deleteConfirmationResponce } from 'src/app/core/util/deepCopy';
import { showFeedbackSaved } from 'src/app/core/util/notification';
import { Globals } from 'src/app/globals';
import { DeletionModalComponent } from 'src/app/shared/components/modal/deletion-modal/deletion-modal.component';

@Component({
  selector: 'app-context-detail',
  templateUrl: './context-detail.component.html',
  styleUrls: ['./context-detail.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContextDetailComponent extends ParentTabbedPage {
  isLoading: boolean = true;
  isProcessing: boolean = false;
  showNotification: boolean = false;
  hasSuggestedStakeholders: boolean = false;
  public context: any = null;
  types: any[] = [];
  stakeholders: any[] = [];
  issues: any[] = [];
  complaints: any[] = [];
  requirements: any[] = [];
  contactmoments: any[] = [];
  documents: any[] = [];
  permits: any[] = [];
  tasks: any[] = [];
  filteredStakeholders: any[] = [];
  suggestedStakeholders: any[] = [];
  contextTableData: {label: string, value: any}[] = [];
  pageTitle = "contexts";
  pageSubTitle = "Terug naar overzicht";
  backToOverview = true;

  // For add-connect-generic component
  entities: any;
  loaded: boolean = false;

  skippedKeys = [
    'hash_id',
    'id',
    'datasource_id',
    'api_id',
    'created_at',
    'project_id',
    'followers_count',
    'created_at',
    'updated_at'
  ];

  constructor(private dialogRef: MatDialogRef<ContextDetailComponent>, private dialog: MatDialog, public router: Router, @Inject(MAT_DIALOG_DATA) public data: any, private _contextsService: ContextService, public cdRef: ChangeDetectorRef, private cachedService: CachedService, public globals: Globals, public pubsub: PubsubService){
    super(pubsub, globals, cdRef);
  }

  ngOnInit(): void {
    this._contextsService.getContextTypes().subscribe((types)=>{
      this.types = types;
      this.getSuggestedStakeholders();
    })
  }

  flattenObject(obj: any, parentKey: string = ''): { label: string; value: any }[] {
    let result: { label: string; value: any }[] = [];

    for (let key in obj) {
      if (!obj.hasOwnProperty(key) || this.skippedKeys.indexOf(key) !== -1) continue;

      // let newKey = parentKey ? `${parentKey}.${key}` : key;
      const value = obj[key];

      if (!value) {
        continue;
      }

      const newKey = key.replaceAll('_', ' ');

      if (typeof value === 'object' && value !== null) {
        result.push(...this.flattenObject(value, newKey));
      } else {
        result.push({ label: newKey, value: value });
      }
    }

    return result;
  }

  getSuggestedStakeholders(){
    // this._contextsService.getIrrelevantContextItems().subscribe((res: any)=>{
      // const IC: any = res.filter(c => c?.uuid === this.data.id);
      const IC: any = [{...this.data.complete_item}];
      if(!IC.length){
        this.loadContext();
        return;
      }
      let suggested = IC[0]?.suggested_stakeholders ?? [];
      suggested = [...suggested, ...(IC[0].metadata?.suggested_stakeholders ?? [])];
      this.data.suggested_stakeholders = [];
      this.data.locations = IC[0]?.locations ?? [];
      this.data.metadata = IC[0]?.metadata ?? {};

      if (suggested && suggested.length>0) {
        suggested.forEach((item, i) => {
          item.id = 'suggested-'+i;
          item.is_suggested = true;
          const {id, is_suggested, name } = item;
          this.stakeholders.push({id, is_suggested, name });
          this.data.suggested_stakeholders.push({id, is_suggested, name });
        });
      }
      this.loadContext();
    // })
  }

  initContext(){
    const {link, lines, locatable_type, locatable_id, points, polygons, types, ...data} = this.data?.locations
    const locations = {link, lines, locatable_type, locatable_id, points, polygons, types};
    const updatedTypes = this.types.filter(type => type.name===(types[0].name || ''));
    const context = {
      id: this.data.id,
      text: this.data.name,
      description: this.data.name,
      is_relevant: false,
      source: 'api',
      stakeholders: this.data?.suggested_stakeholders,
      issues: [],
      complaints: [],
      locations: locations,
      metadata: this.data.metadata,
      requirements: [],
      contactmoments: [],
      documents: [],
      types: updatedTypes,
      subprojects: [],
      permits: [],
      tasks: [],
      users: [],
      workflows: []
    };
    return context;
  }

  loadContext(){
    this.isLoading = true;
    this.loaded = false;
    forkJoin({
      context: (this.data.locatable_type === 'irrelevant_contexts') ? of(this.initContext()) : this._contextsService.getContext(this.data.id),
      relations: forkJoin([
        this.cachedService.getIssuesRelations(),
        this.cachedService.getDecisionsRelations(),
        this.cachedService.getLogsRelations(),
        this.cachedService.getStakeholdersRelations(),
        this.cachedService.getPermitsRelations(),
        this.cachedService.getDocumentsRelations(),
      ])
    }).subscribe(({ context, relations }) => {
      this.context = context;      
      const [issues, requirements, contactmoments, stakeholders, permits, documents] = relations;
      
      // Build entities object for add-connect-generic component
      this.entities = {
        stakeholders,
        issues,
        requirements,
        contactmoments,
        documents,
        permits
      };
    
      this.loaded = true;
      this.isLoading = false;
      this.cdRef.detectChanges();
    });
  }

  close(){
    this.pubsub.closeModal(this.dialogRef);
    this.pubsub.resetHistory();
  }

  updateEntities(){
    if (!this.context?.text?.trim() || !this.context?.description?.trim() || !this.context?.types?.length) {
      return;
    }
    if (this.context.source==='api') {
      this.cachedService.getStakeholdersRelations().subscribe((res)=>{
        this.filteredStakeholders = this.context.stakeholders.filter(item => !item?.is_suggested);
        this.suggestedStakeholders = this.context.stakeholders.filter(item => item?.is_suggested);
        this.stakeholders = this.stakeholders.filter((stk) => {
          return !(
            stk.is_suggested &&
            stk.id.toString().includes('suggested') &&
            !this.context.stakeholders.some(s => s.id === stk.id)
          );
        });
        this.updateStakeholdersList();
        if (!this.context.metadata) {
          this.context.metadata = {};
        }
        this.context.metadata.suggested_stakeholders = this.suggestedStakeholders;
        this.isProcessing = true;
        this.updateContext(this.context);
      })
    } else {
      this.isProcessing = true;
      this.updateContext(this.context);
    }
  }

  updateContext(context){
    if (context.is_relevant===false) {
      const {id,is_relevant, stakeholders, metadata, text, ...data} = context;
      const updatedContext = {metadata, text, uuid: id, stakeholders: this.filteredStakeholders, ...data};
      this._contextsService.createContext(updatedContext).subscribe((res)=>{
        context = res;
        this.context = res;
        // this.context.stakeholders = [...this.context.stakeholders.map(({ id, code, name }) => ({ id, code, name })), ...this.suggestedStakeholders];
        this.data.id = res.id;
        this.callUpdateApi(this.context);
      })
    } else if(this.context.source==='api' && this.suggestedStakeholders.length>0){
      const {stakeholders, ...data} = context;
      this.callUpdateApi({stakeholders: this.filteredStakeholders,...data});
    } else {
      this.callUpdateApi(context)
    }
  }

  callUpdateApi(context){
    this._contextsService.updateContext(this.data.id, context).subscribe((res)=>{
      this.context = res;
      // this.context.stakeholders = res.source==='api' && this.suggestedStakeholders.length ? [...this.context.stakeholders.map(({ id, code, name }) => ({ id, code, name })), ...this.suggestedStakeholders] : [...this.context.stakeholders.map(({ id, code, name }) => ({ id, code, name }))];
      this.cdRef.detectChanges();
      showFeedbackSaved();
      // this.updateContextsOnMap();
    })
  }

  deleteContext(){
    const dialogRef = this.dialog.open(DeletionModalComponent, {
      data: deleteConfirmationResponce(this.context, this.router, 'contexts'),
    });
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe((remove: boolean) => {
      if (remove) {
        this._contextsService.deleteContext(this.data.id).subscribe(()=>{
          this._contextsService.setContextTypeUpdated({flag: true, type: this.context?.types?.map((t: any) => t.name)});
          this.close();
          showFeedbackSaved();
        })
      }
    });
  }

  updateContextsOnMap(){
    this._contextsService.setContextTypeUpdated({flag: true, type: this.context?.types?.map((t: any) => t.name)});
  }

  updateStakeholdersList(){
    this.stakeholders = this.stakeholders.map(stk => {
      if (this.filteredStakeholders.some(f => f.id === stk.id)) {
        const { is_suggested, ...rest } = stk;
        return rest;
      }
      return stk;
    });
  }

  onSelectTheme(event){
    this.context.types = event.value;
    this.callUpdateApi(this.context);
  }

  updateEntitiesFromGeneric(event: any) {
    this.context[event.type] = event.selected;
    this.callUpdateApi(this.context);
  }
}
